import {
  useEffect,
  useState,
  type ComponentType,
  type FocusEvent,
} from 'react';

import { BIND_PREFIX } from './constants';
import { isFieldCheckOperator } from './field-check';

/** Asks every held field check to show its error. */
export const FIELD_CHECK_REVEAL_EVENT = 'de-field-check-reveal';

export function requestFieldCheckReveal(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(FIELD_CHECK_REVEAL_EVENT));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFieldCheckBind(value: unknown): boolean {
  return isRecord(value) && isFieldCheckOperator(value.operator);
}

function hasFieldCheckBind(
  rawProps: Readonly<Record<string, unknown>>,
  propName: 'error' | 'errorMessage',
): boolean {
  if (isFieldCheckBind(rawProps[`${BIND_PREFIX}${propName}`])) return true;
  const bind = rawProps._bind;
  return isRecord(bind) && isFieldCheckBind(bind[propName]);
}

/**
 * Input and Textarea paint a field-check error as soon as the answer
 * fails. Hold that paint until the visitor leaves the field or continues.
 * Authored checks use flat `_bind.error` keys. A nested `_bind` object is
 * accepted for the same props.
 */
export function holdsFieldCheckUntilRevealed(
  componentName: string,
  rawProps: Readonly<Record<string, unknown>>,
): boolean {
  if (componentName !== 'Input' && componentName !== 'Textarea') return false;
  return (
    hasFieldCheckBind(rawProps, 'error') ||
    hasFieldCheckBind(rawProps, 'errorMessage')
  );
}

/**
 * Keeps `error` and `errorMessage` quiet until the visitor leaves the field
 * with an answer in it, or {@link requestFieldCheckReveal}. After that, the
 * live check shows until the answer passes. Leaving the field empty, or a
 * reload, starts quiet again.
 */
export function FieldCheckErrorHold<P extends object>({
  component: Component,
  props,
}: {
  readonly component: ComponentType<P>;
  readonly props: P;
}): JSX.Element {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const reveal = () => setRevealed(true);
    window.addEventListener(FIELD_CHECK_REVEAL_EVENT, reveal);
    return () => window.removeEventListener(FIELD_CHECK_REVEAL_EVENT, reveal);
  }, []);

  const record = props as Record<string, unknown>;
  const shown = revealed
    ? record
    : { ...record, error: false, errorMessage: '' };
  const onBlur = (event: FocusEvent<HTMLElement>) => {
    // Passing through an empty field must not arm the message, or the first
    // characters typed on the way back would show it mid-typing. Continue
    // still reveals every field through the reveal event above.
    const field = event.target as HTMLInputElement | HTMLTextAreaElement;
    setRevealed(typeof field.value === 'string' && field.value !== '');
    const authored = record.onBlur;
    if (typeof authored === 'function') {
      (authored as (next: FocusEvent<HTMLElement>) => void)(event);
    }
  };

  return <Component {...({ ...shown, onBlur } as P)} />;
}
